import { useState } from 'react';
import { Button, Modal, Radio, RadioGroup, Typography } from '@trackman/web-shared-components';
import { useTranslation } from 'react-i18next';
import { useReportId } from 'hooks/helperHooks';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import { useTelemetry } from 'hooks/useTelemetry';
import { AppPermissions } from 'constants/AppPermissions';
import Alert from 'components/Basic/Alert/Alert';
import { useReportViewModeStore } from 'stores/ReportViewModeStore';

type SaveMethod = 'session' | 'organization';

type Props = {
  setIsEditMode: (value: boolean) => void;
  saveLayoutForSession: () => void;
  saveLayoutForOrg: () => void;
  hasUnsavedEdits: boolean;
  discardLayoutChanges: () => void;
};

const EditLayoutActions = ({ setIsEditMode, saveLayoutForSession, saveLayoutForOrg, hasUnsavedEdits, discardLayoutChanges }: Props) => {
  const { t } = useTranslation();
  const { isCreate } = useReportId();
  const { IsAllowed } = useApplicationUserRightsStore();
  const { setShowShelfForSingleViewMode } = useReportViewModeStore();
  const { trackEvent, events } = useTelemetry();

  const [isSaveForOrgModalOpen, setIsSaveForOrgModalOpen] = useState(false);
  const [isDiscardChangesModalOpen, setIsDiscardChangesModalOpen] = useState(false);
  const [saveMethod, setSaveMethod] = useState<SaveMethod>(isCreate ? 'organization' : 'session');

  const canEditDefaultLayout = IsAllowed([AppPermissions.DefaultLayoutEdit]);

  const handleSave = () => {
    if (canEditDefaultLayout) {
      setSaveMethod(isCreate ? 'organization' : 'session');
      setIsSaveForOrgModalOpen(true);
    } else {
      saveLayoutForSession();
      setIsEditMode(false);
      setShowShelfForSingleViewMode(false);
      trackEvent(events.SavedReportLayoutForSession);
    }
  };

  const handleConfirmSaveFromOrgModal = () => {
    if (saveMethod === 'session') {
      saveLayoutForSession();
      trackEvent(events.SavedReportLayoutForSession);
    } else {
      saveLayoutForOrg();
      trackEvent(events.SavedReportLayoutForOrg);
    }
    setIsSaveForOrgModalOpen(false);
    setIsEditMode(false);
    setShowShelfForSingleViewMode(false);
  };

  const handleCancel = () => {
    if (hasUnsavedEdits) {
      setIsDiscardChangesModalOpen(true);
    } else {
      setIsEditMode(false);
      setShowShelfForSingleViewMode(false);
    }
  };

  const handleDiscardChanges = () => {
    setIsDiscardChangesModalOpen(false);
    setIsEditMode(false);
    discardLayoutChanges();
    setShowShelfForSingleViewMode(false);
    trackEvent(events.DiscardedReportLayoutChanges);
  };

  return (
    <>
      <Button key='cancel' label={t('Cancel')} intent='default' onClick={handleCancel} style={{ width: '100%' }} />
      <Button label={t('Save')} intent='primary' onClick={handleSave} disabled={!hasUnsavedEdits} style={{ width: '100%' }} />

      {/* Save Modal */}
      <Modal
        title={t('save.modal.title')}
        open={isSaveForOrgModalOpen}
        onClose={() => setIsSaveForOrgModalOpen(false)}
        confirmBtn={{ label: t('save.modal.confirmBtn'), onClick: handleConfirmSaveFromOrgModal }}
        cancelBtn={{ label: t('Cancel'), onClick: () => setIsSaveForOrgModalOpen(false) }}
        style={{ zIndex: 2001 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography component='div'>{t('save.modal.body')}</Typography>
          <RadioGroup style={{ gap: '16px' }} value={saveMethod} onChange={(e) => setSaveMethod(e.target.value as SaveMethod)}>
            <Radio
              value='session'
              label={t('save.modal.sessionOptionLabel')}
              hint={t('save.modal.sessionOptionHint')}
              disabled={isCreate}
            />
            <Radio value='organization' label={t('save.modal.orgOptionLabel')} hint={t('save.modal.orgOptionHint')} />
          </RadioGroup>
          <Alert variant='warning' message={t('save.modal.warning')} />
        </div>
      </Modal>

      {/* Discard Modal */}
      <Modal
        title={t('cancel.modal.title')}
        open={isDiscardChangesModalOpen}
        onClose={() => setIsDiscardChangesModalOpen(false)}
        confirmBtn={{ label: t('cancel.modal.confirmBtn'), onClick: handleDiscardChanges }}
        cancelBtn={{ label: t('cancel.modal.cancelBtn'), onClick: () => setIsDiscardChangesModalOpen(false) }}
        style={{ zIndex: 2001 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography component='div'>{t('cancel.modal.body')}</Typography>
        </div>
      </Modal>
    </>
  );
};

export default EditLayoutActions;
